'use client';

import { useEffect, useRef, useState } from 'react';
import { trpc } from '@/lib/trpc';

interface UseSessionRecoveryOptions {
  recoveryToken: string | null;
  isRedirectReturn: boolean;
  isAuthenticated: boolean;
}

interface UseSessionRecoveryResult {
  /** Recovery was attempted (or skipped because not needed) */
  attempted: boolean;
  /** Session was successfully restored */
  recovered: boolean;
}

export function useSessionRecovery({
  recoveryToken,
  isRedirectReturn,
  isAuthenticated,
}: UseSessionRecoveryOptions): UseSessionRecoveryResult {
  const [verifyResult, setVerifyResult] = useState<'pending' | 'success' | 'failure'>('pending');
  const verifyingRef = useRef(false);
  const utils = trpc.useUtils();

  const needsVerification = isRedirectReturn && !isAuthenticated && !!recoveryToken;

  useEffect(() => {
    if (!needsVerification || !recoveryToken || verifyingRef.current) return;
    verifyingRef.current = true;

    fetch('/api/auth/one-time-token/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token: recoveryToken }),
    })
      .then(async (res) => {
        if (res.ok) {
          await utils.authMe.me.invalidate();
          setVerifyResult('success');
        } else {
          setVerifyResult('failure');
        }
      })
      .catch(() => {
        setVerifyResult('failure');
      });
  }, [needsVerification, recoveryToken, utils]);

  return {
    attempted: needsVerification ? verifyResult !== 'pending' : true,
    recovered: verifyResult === 'success',
  };
}
